import { createAction } from '@reduxjs/toolkit';
import { Map as ImmutableMap, type List, type RecordOf } from 'immutable';
import { createAction as reduxActionsCreator } from 'redux-actions';
import { v4 as uuid } from 'uuid';

import type { Account } from '../accounts/accountsTypes';
import { getPayloadCreator, type Meta } from '../core/actionHelpers';
import { institutionLoginsActions } from '../institution-logins';
import type { InstitutionLogin, MfaChallenge, MfaResponse } from '../institution-logins/institutionLoginsTypes';
import type { Institution } from '../institutions/institutionsTypes';
import { rootUiActions, rootUiTypes } from '../root-ui';
import type { PlaidItemEvent } from './accountDiscoveryTransformers';
import {
  type AccountDiscoveryData,
  type AccountDiscoveryMode,
  type AccountDiscoveryStatus,
  type AccountDiscoveryUpdateStrategy,
  type AggAction,
  type AggregationInfoRequest,
  DIALOG_TYPE_ACCOUNTS_DISCOVERY,
  type DiscoveryInstitutionLogin,
  type InstitutionLoginUpserterDataProps,
  mkAccountDiscoveryDialogProps,
  type ResponseError,
} from './accountDiscoveryTypes';

type AccountDiscoveryDialogOptions = {
  initiator?: string;
  mode?: AccountDiscoveryMode;
  rootScreen?: string | null;
  onClose?: (() => void) | null;
  institutionId?: string | null;
};

type AccountRediscoveryOptions = AccountDiscoveryDialogOptions & {
  dialogTitle?: string | null;
};

type RootScreenOptions = {
  rootScreen?: string | null;
  initiator?: string;
};

type UpdateFiPayload = {
  institutionLogin: InstitutionLogin;
  clearInstitution?: boolean;
};

type RootUiDialogAction = ReturnType<typeof rootUiActions.createDialog>;
type DialogActionCreator<Args extends unknown[]> = ((...args: Args) => RootUiDialogAction) & { toString(): string };

// redux-actions creators keep their toString() (used as handleActions/takeLatest keys); this cast
// only narrows the loose redux-actions typing to the precise argument tuple and dialog action type.
const toDialogActionCreator = <Args extends unknown[]>(actionCreator: { toString(): string }) =>
  actionCreator as DialogActionCreator<Args>;

// Account-discovery dispatches a single "response" action for both success and failure; the failure
// case carries an Error. redux-actions set the FSA `error` flag automatically for Error payloads, RTK
// does not — restore it so the reducer's success/error branches (which narrow on `payload instanceof
// Error`) keep behaving identically to the pre-conversion runtime.
const getResponsePayloadCreator =
  <Payload>() =>
  (payload: Payload | Error | ResponseError, meta?: Meta) => ({
    payload,
    meta,
    ...(payload instanceof Error && { error: true as const }),
  });

export const accountDiscoverySetup = createAction(
  'ACCOUNT_DISCOVERY_SETUP',
  getPayloadCreator<Partial<InstitutionLoginUpserterDataProps> | undefined>(),
);
export const accountDiscoveryCleanup = createAction('ACCOUNT_DISCOVERY_CLEANUP', getPayloadCreator<null>());

export const institutionSelected = createAction(
  'ACCOUNT_DISCOVERY_INSTITUTION_SELECTED',
  getPayloadCreator<{ channel?: string; institution?: Institution | RecordOf<Institution> }>(),
);

export const accountDiscoveryCredentialsFormSubmit = createAction(
  'ACCOUNT_DISCOVERY_CREDENTIALS_FORM_SUBMIT',
  getPayloadCreator<{ institutionLogin: DiscoveryInstitutionLogin }>(),
);
export const accountDiscoveryCredentialsFormSubmitResponse = createAction(
  'ACCOUNT_DISCOVERY_CREDENTIALS_FORM_SUBMIT_SUCCESS',
  getResponsePayloadCreator<{ institutionLogin: InstitutionLogin }>(),
);

export const accountDiscoveryStatus = createAction(
  'ACCOUNT_DISCOVERY_STATUS',
  getPayloadCreator<{ status: AccountDiscoveryStatus }>(),
);

export const accountDiscoveryMfaRequired = createAction(
  'ACCOUNT_DISCOVERY_MFA_REQUIRED',
  getPayloadCreator<{ mfaChallenge: MfaChallenge | RecordOf<MfaChallenge> }>(),
);
export const accountDiscoveryMfaFormSubmit = createAction(
  'ACCOUNT_DISCOVERY_MFA_FORM_SUBMIT',
  getPayloadCreator<{ mfaResponse: MfaResponse }>(),
);
export const accountDiscoveryMfaFormSubmitResponse = createAction(
  'ACCOUNT_DISCOVERY_MFA_FORM_SUBMIT_RESPONSE',
  getResponsePayloadCreator<Record<string, never>>(),
);

export const getPartnerAuthUris = createAction(
  'GET_PARTNER_AUTH_URIS',
  getPayloadCreator<{ partnerAuth: { intuitProperty: string; partnerUid: string }; redirectUri: string }>(),
);
export const getPartnerAuthUrisSuccess = createAction(
  'GET_PARTNER_AUTH_URIS_SUCCESS',
  getPayloadCreator<{ partnerAuthUris: List<string> }>(),
);
export const getPartnerAuthUrisFailure = createAction(
  'GET_PARTNER_AUTH_URIS_FAILURE',
  getPayloadCreator<{ error?: ResponseError }>(),
);

export const getAggregationInfo = createAction('GET_AGGREGATION_INFO', getPayloadCreator<AggregationInfoRequest>());

export const accountDiscoverySuccessWithAccounts = createAction(
  'ACCOUNT_DISCOVERY_SUCCESS_WITH_ACCOUNTS',
  getPayloadCreator<{ institutionLogin: InstitutionLogin; accountDiscoveryData: AccountDiscoveryData }>(),
);
export const accountDiscoverySuccess = createAction(
  'ACCOUNT_DISCOVERY_SUCCESS',
  getPayloadCreator<{ institutionLogin: InstitutionLogin }>(),
);

export const accountDiscoveryFailure = createAction(
  'ACCOUNT_DISCOVERY_FAILURE',
  getPayloadCreator<{ adStatus: AccountDiscoveryStatus; institutionLogin: InstitutionLogin }>(),
);
export const accountDiscoveryStopped = createAction(
  'ACCOUNT_DISCOVERY_STOPPED',
  getPayloadCreator<{ adStatus?: AccountDiscoveryStatus; institutionLogin?: InstitutionLogin | null }>(),
);
export const accountDiscoveryError = createAction(
  'ACCOUNT_DISCOVERY_ERROR',
  getPayloadCreator<{ institutionLogin?: InstitutionLogin; error?: ResponseError }>(),
);

// A Stripe connection has no accounts to send.
export const discoverInstitutionLoginWithAccounts = createAction(
  'DISCOVER_INSTITUTION_LOGIN_WITH_ACCOUNTS',
  getPayloadCreator<{ institutionLogin: DiscoveryInstitutionLogin; accounts?: Account[] }>(),
);
export const discoverInstitutionLoginWithAccountsByPolling = createAction(
  'DISCOVER_INSTITUTION_LOGIN_WITH_ACCOUNTS_BY_POLLING',
  getPayloadCreator<{ institutionLogin: DiscoveryInstitutionLogin; pollingRef: string }>(),
);
export const discoverInstitutionLoginWithAccountsResponse = createAction(
  'DISCCOVER_INSTITUTION_LOGIN_WITH_ACCOUNTS_RESPONSE',
  getResponsePayloadCreator<{
    institutionLogin: DiscoveryInstitutionLogin;
    accountDiscoveryData: AccountDiscoveryData;
    updateStrategy: AccountDiscoveryUpdateStrategy;
  }>(),
);
export const discoverInstitutionLoginWithAccountsDiscard = createAction(
  'DISCCOVER_INSTITUTION_LOGIN_WITH_ACCOUNTS_DISCARD',
  getPayloadCreator<null>(),
);

export const accountDiscoveryWidgetUpdateSuccess = createAction(
  'ACCOUNT_DISCOVERY_WIDGET_UPDATE_SUCCESS',
  getPayloadCreator<{
    institutionLogin?: InstitutionLogin;
    institutionLoginToUpdate?: DiscoveryInstitutionLogin | null;
    accountDiscoveryData: AccountDiscoveryData | null;
  }>(),
);

export const logPlaidItemEvent = createAction(
  'ACCOUNT_DISCOVERY_LOG_PLAID_ITEM_EVENT',
  getPayloadCreator<{
    event: PlaidItemEvent;
    institution: Institution | null | undefined;
    institutionLogin: DiscoveryInstitutionLogin | null | undefined;
  }>(),
);
export const logPlaidItemEventResponse = createAction(
  'ACCOUNT_DISCOVERY_LOG_PLAID_ITEM_EVENT_RESPONSE',
  getResponsePayloadCreator<null>(),
);

export const doAccountDiscovery = toDialogActionCreator<[AccountDiscoveryDialogOptions?, unknown?]>(
  reduxActionsCreator(
    rootUiActions.createDialog.toString(),
    (options?: AccountDiscoveryDialogOptions) =>
      rootUiTypes.mkRootUiData({
        id: uuid(),
        type: DIALOG_TYPE_ACCOUNTS_DISCOVERY,
        allowNesting: true,
        props: ImmutableMap(
          mkAccountDiscoveryDialogProps({
            initiator: options?.initiator || 'unknown',
            mode: options?.mode || 'ADD-FI',
            rootScreen: options?.rootScreen || null,
            onClose: options?.onClose || null,
            institutionId: options?.institutionId || null,
          }),
        ),
      }),
    (_options: AccountDiscoveryDialogOptions | undefined, meta: unknown) => meta,
  ),
);

export const doAccountRediscovery = toDialogActionCreator<
  [InstitutionLogin | null | undefined, (AccountRediscoveryOptions | null)?, AggAction?]
>(
  reduxActionsCreator(
    rootUiActions.createDialog.toString(),
    (institutionLogin: InstitutionLogin | null | undefined, options?: AccountRediscoveryOptions | null) =>
      rootUiTypes.mkRootUiData({
        id: uuid(),
        type: DIALOG_TYPE_ACCOUNTS_DISCOVERY,
        allowNesting: true,
        props: ImmutableMap(
          mkAccountDiscoveryDialogProps({
            initiator: options?.initiator || 'unknown',
            mode: 'ACCOUNT-REDISCOVERY',
            customDialogTitle: options?.dialogTitle || null,
            rootScreen: options?.rootScreen || null,
            onClose: options?.onClose || null,
            institutionLogin,

            afterCloseAction: institutionLoginsActions.refreshAccounts({ institutionLoginId: institutionLogin?.id }),
          }),
        ),
      }),
    (
      _institutionLogin: InstitutionLogin | null | undefined,
      options: AccountRediscoveryOptions | null | undefined,
      _meta: unknown,
    ) => options,
  ),
);

export const doProvideCredentialsAndRefresh = toDialogActionCreator<
  [InstitutionLogin | null | undefined, (RootScreenOptions | null)?, AggAction?]
>(
  reduxActionsCreator(
    rootUiActions.createDialog.toString(),
    (
      institutionLogin: InstitutionLogin | null | undefined,
      options: RootScreenOptions | null = null,
      aggAction?: AggAction,
    ) =>
      rootUiTypes.mkRootUiData({
        id: uuid(),
        type: DIALOG_TYPE_ACCOUNTS_DISCOVERY,
        allowNesting: true,
        props: ImmutableMap(
          mkAccountDiscoveryDialogProps({
            initiator: options?.initiator || 'unknown',
            rootScreen: options?.rootScreen || null,
            mode: 'PROVIDE-CREDENTIALS',
            institutionLogin,
            aggAction,

            afterCloseAction: institutionLoginsActions.refreshAccounts({ institutionLoginId: institutionLogin?.id }),
          }),
        ),
      }),
    (
      _institutionLogin: InstitutionLogin | null | undefined,
      options: RootScreenOptions | null,
      _aggAction: AggAction | undefined,
      _meta: unknown,
    ) => options,
  ),
);

export const doReAuthenticate = toDialogActionCreator<
  [InstitutionLogin | null | undefined, (RootScreenOptions | null)?, AggAction?]
>(
  reduxActionsCreator(
    rootUiActions.createDialog.toString(),
    (
      institutionLogin: InstitutionLogin | null | undefined,
      options: RootScreenOptions | null = null,
      aggAction?: AggAction,
    ) =>
      rootUiTypes.mkRootUiData({
        id: uuid(),
        type: DIALOG_TYPE_ACCOUNTS_DISCOVERY,
        allowNesting: true,
        props: ImmutableMap(
          mkAccountDiscoveryDialogProps({
            mode: 'RE-AUTHENTICATE',
            rootScreen: options?.rootScreen || null,
            institutionLogin,
            aggAction,

            afterCloseAction: institutionLoginsActions.refreshAccounts({ institutionLoginId: institutionLogin?.id }),
          }),
        ),
      }),
  ),
);

export const doUpdateCredentials = toDialogActionCreator<
  [InstitutionLogin | null | undefined, (RootScreenOptions | null)?, AggAction?]
>(
  reduxActionsCreator(
    rootUiActions.createDialog.toString(),
    (
      institutionLogin: InstitutionLogin | null | undefined,
      options: RootScreenOptions | null = null,
      aggAction?: AggAction,
    ) =>
      rootUiTypes.mkRootUiData({
        id: uuid(),
        type: DIALOG_TYPE_ACCOUNTS_DISCOVERY,
        allowNesting: true,
        props: ImmutableMap(
          mkAccountDiscoveryDialogProps({
            mode: 'UPDATE-CREDENTIALS',
            rootScreen: options?.rootScreen || null,
            institutionLogin,
            aggAction,

            afterCloseAction: institutionLoginsActions.refreshAccounts({ institutionLoginId: institutionLogin?.id }),
          }),
        ),
      }),
  ),
);

export const doUpdateFi = toDialogActionCreator<[UpdateFiPayload, (RootScreenOptions | null)?, AggAction?]>(
  reduxActionsCreator(
    rootUiActions.createDialog.toString(),
    (
      { institutionLogin, clearInstitution = false }: UpdateFiPayload,
      options: RootScreenOptions | null = null,
      aggAction?: AggAction,
    ) =>
      rootUiTypes.mkRootUiData({
        id: uuid(),
        type: DIALOG_TYPE_ACCOUNTS_DISCOVERY,
        allowNesting: true,
        props: ImmutableMap(
          mkAccountDiscoveryDialogProps({
            mode: 'UPDATE-FI',
            rootScreen: options?.rootScreen || null,
            institutionLogin: clearInstitution
              ? institutionLogin.merge({ institutionId: null, cpInstitutionId: null, brandingId: null })
              : institutionLogin,
            aggAction,

            // afterCloseAction: institutionLoginsActions.refreshAccounts({ institutionLoginId: institutionLogin?.id }),
          }),
        ),
      }),
  ),
);

export const doMigrateFi = toDialogActionCreator<
  [InstitutionLogin | null | undefined, (RootScreenOptions | null)?, AggAction?, boolean?]
>(
  reduxActionsCreator(
    rootUiActions.createDialog.toString(),
    (
      institutionLogin: InstitutionLogin | null | undefined,
      options: RootScreenOptions | null = null,
      aggAction?: AggAction,
      aggActionShowIntro?: boolean,
    ) =>
      rootUiTypes.mkRootUiData({
        id: uuid(),
        type: DIALOG_TYPE_ACCOUNTS_DISCOVERY,
        allowNesting: true,
        props: ImmutableMap(
          mkAccountDiscoveryDialogProps({
            mode: 'MIGRATE-INSTITUTION-LOGIN',
            rootScreen: options?.rootScreen || null,
            institutionLogin,
            aggAction,
            aggActionShowIntro,
          }),
        ),
      }),
  ),
);
